import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Theme, getTheme, defaultTheme, getAllThemes } from '../themes/themeConfig';

/** Legacy JS theme picker for charts + Profile. Surfaces under `body.ma-miniapp` use MaStyleStack CSS vars instead. */

interface ThemeContextType {
  currentTheme: Theme;
  setTheme: (themeName: string) => void;
  availableThemes: Theme[];
  isDark: boolean;
  animatedBgEnabled: boolean;
  setAnimatedBgEnabled: (enabled: boolean) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [currentThemeName, setCurrentThemeName] = useState<string>(() => {
    // Get theme from localStorage or use default
    return localStorage.getItem('selectedTheme') || defaultTheme;
  });

  const [currentTheme, setCurrentTheme] = useState<Theme>(() => {
    return getTheme(currentThemeName);
  });

  const getInitialAnimatedBg = (): boolean => {
    try {
      const saved = localStorage.getItem('animatedBgEnabled');
      if (saved === '0' || saved === 'false') return false;
      if (saved === '1' || saved === 'true') return true;
    } catch {}
    try {
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return false;
      }
    } catch {}
    return true;
  };

  const [animatedBgEnabled, setAnimatedBgEnabledState] = useState<boolean>(getInitialAnimatedBg);

  const setTheme = (themeName: string) => {
    const newTheme = getTheme(themeName);
    // Add a temporary class to animate theme transition
    try {
      const htmlEl = document.documentElement;
      htmlEl.classList.add('theme-transitioning');
      setTimeout(() => { try { htmlEl.classList.remove('theme-transitioning'); } catch {} }, 500);
    } catch {}
    setCurrentTheme(newTheme);
    setCurrentThemeName(themeName);
    localStorage.setItem('selectedTheme', themeName);
    // Apply theme to document root
    applyThemeToDocument(newTheme);
  };

  const applyAnimatedClass = (enabled: boolean) => {
    const htmlEl = document.documentElement;
    if (!htmlEl) return;
    if (enabled) {
      htmlEl.classList.add('animated-bg');
    } else {
      htmlEl.classList.remove('animated-bg');
    }
  };

  const setAnimatedBgEnabled = (enabled: boolean) => {
    setAnimatedBgEnabledState(enabled);
    try { localStorage.setItem('animatedBgEnabled', enabled ? '1' : '0'); } catch {}
    applyAnimatedClass(enabled);
  };

  const applyThemeToDocument = (theme: Theme) => {
    if (typeof document !== 'undefined' && document.body.classList.contains('ma-miniapp')) {
      return;
    }
    const root = document.documentElement;
    
    // Apply all theme colors as CSS custom properties
    Object.entries(theme.colors).forEach(([key, value]) => {
      root.style.setProperty(`--theme-${key}`, value);
    });
    
    // Apply theme-specific properties
    root.style.setProperty('--theme-name', theme.name);
    root.style.setProperty('--theme-display-name', theme.displayName);
    root.style.setProperty('--theme-is-dark', theme.isDark ? '1' : '0');
    
    // Update html (root) class for theme-specific styling
    const htmlEl = document.documentElement;
    htmlEl.className = htmlEl.className.replace(/\btheme-[\w-]+\b/g, '').trim();
    htmlEl.classList.add(`theme-${theme.name}`);
    
    // <meta theme-color> is owned by index.html (Oroboros ground). The Jester theme presets are
    // kernel-only and must not repaint the browser chrome with their own backgrounds.
  };

  // Initialize theme on mount
  useEffect(() => {
    applyThemeToDocument(currentTheme);
    applyAnimatedClass(animatedBgEnabled);
  }, []);

  // Update theme when currentTheme changes
  useEffect(() => {
    applyThemeToDocument(currentTheme);
  }, [currentTheme]);

  // Update animated class when toggled
  useEffect(() => {
    applyAnimatedClass(animatedBgEnabled);
  }, [animatedBgEnabled]);

  const value: ThemeContextType = {
    currentTheme,
    setTheme,
    availableThemes: getAllThemes(),
    isDark: currentTheme.isDark,
    animatedBgEnabled,
    setAnimatedBgEnabled,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Hook for getting specific theme colors
export const useThemeColors = () => {
  const { currentTheme } = useTheme();
  return currentTheme.colors;
};

// Hook for checking if current theme is dark
export const useIsDarkTheme = () => {
  const { isDark } = useTheme();
  return isDark;
};